<template>
  <div class="absolute top-[40px] w-[400px] left-0 h-[500px] bg-white z-[100] p-4 shadow-xl rounded-xl border border-gray-100 overflow-y-auto">
    <button @click="$emit('close')"   class="absolute top-3 right-3 text-gray-400 hover:text-gray-700 text-lg transition-colors">
      <i class="fas fa-times"></i>
    </button>
    
    <div class="mb-3 flex items-center gap-2">
      <span class="bg-gradient-to-r from-green-500 to-green-600 text-white rounded-full px-3 py-1 font-semibold text-xs shadow-sm">
        {{ player?.player?.player_name || player?.player_name }}
      </span>
    </div>

    <div class="text-xl font-bold mb-3 flex items-center gap-2">
      {{ statLabel }} Adjustment
      <span v-if="hasThresholdAlert" class="inline-flex items-center justify-center w-6 h-6 bg-red-500 text-white text-sm font-bold rounded-full animate-pulse" title="Threshold alert: Significant deviation detected">
        !
      </span>
      <button 
        @click="saveChanges" 
        class="ml-auto bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white px-4 py-1.5 rounded-lg font-semibold text-sm shadow-sm transition-all hover:shadow-md"
      >
        Save Changes
      </button>
    </div>

    <hr class="my-3 border-gray-100" />

    <div class="space-y-2">
      <StatInputField
        v-model="trim"
        label="Trim per 30"
        @clear="trim = ''"
        :disabled="true"
      />
      <StatInputField
        v-model="manual"
        label="Manual per 30"
        @clear="manual = ''"
      />
      <div class="flex items-center gap-3">
        <div class="w-32 font-medium text-gray-600 text-sm">Auto per 30</div>
        <div class="flex-1 px-3 py-1.5 bg-gray-50 rounded-lg text-gray-700 font-semibold border border-gray-100 text-sm">
          {{ auto }}
        </div>
      </div>
      <div class="flex items-center gap-3">
        <div class="w-32 font-medium text-gray-600 text-sm">Original per 30</div>
        <div class="flex-1 px-3 py-1.5 bg-gray-50 rounded-lg text-gray-700 font-semibold border border-gray-100 text-sm">
          {{ original }}
        </div>
      </div>
    </div>

    <div class="my-4">
      <hr class="border-gray-100" />
      <div class="mt-3">
        <!-- Loading state -->
        <div v-if="loading" class="flex items-center justify-center py-8">
          <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span class="ml-2 text-gray-600">Loading additional stats...</span>

        </div>
        
        <!-- Additional Stats Display -->
         
        <AdditionalStatsDisplay
          v-else-if="additionalStats"
          :additional-stats="additionalStats"
          :stat-key="statKey"
          :stat-per-30-key="statPer30Key"
          :stat-unit="statUnit"
          :original-per-30="original"
          :percentage-difference="percentageDifference"
        />
        
        <!-- Fallback to original TrendCards if no additional stats -->
        <div v-else class="space-y-2">
          <TrendCard
            title="Last 3 per 30"
            percentage="76.95%"
            value="11.59"
            change="-5.04"
            type="warning"
          />
          <TrendCard
            title="Last 6 per 30"
            percentage="24.12%"
            value="8.13"
            change="-1.58"
            type="success"
          />
          <TrendCard
            title="Last 10 per 30"
            percentage="12.21%"
            value="7.35"
            change="-0.80"
            type="success"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, computed, onMounted } from 'vue'
import StatInputField from './StatInputField.vue'
import TrendCard from './TrendCard.vue'
import AdditionalStatsDisplay from './AdditionalStatsDisplay.vue'
import axios from 'axios'
import { apiBalancedProjectionsPath } from '../config/balancedProjectionsApi.js'
import { useRouter } from 'vue-router'
import { useProjectionsStore } from '../stores/projections'
import { useRoute } from 'vue-router'

const route = useRoute()
const projectionsStore = useProjectionsStore()
const router = useRouter()
const props = defineProps({
  player: {
    type: Object,
    required: true
  },
  statKey: {
    type: String,
    required: true
  },
  statLabel: {
    type: String,
    required: true
  },
  position: { type: Object, required: false, default: () => ({ top: 0, left: 0 }) }
})

const emit = defineEmits(['close', 'update:player', 'manual-change'])

// Additional stats state
const additionalStats = ref(null)
const loading = ref(false)
const percentageDifference = ref({})

// Map stat keys to their corresponding per-30 keys and units
const statKeyMapping = {
  'points': { per30Key: 'points_per_30', unit: 'pts' },
  'points_per_30': { per30Key: 'points_per_30', unit: 'pts' },
  'rebounds': { per30Key: 'total_rebounds_per_30', unit: 'reb' },
  'rebounds_per_30': { per30Key: 'total_rebounds_per_30', unit: 'reb' },
  'total_rebounds': { per30Key: 'total_rebounds_per_30', unit: 'reb' },
  'total_rebounds_per_30': { per30Key: 'total_rebounds_per_30', unit: 'reb' },
  'assists': { per30Key: 'assists_per_30', unit: 'ast' },
  'assists_per_30': { per30Key: 'assists_per_30', unit: 'ast' },
  'steals': { per30Key: 'steals_per_30', unit: 'stl' },
  'steals_per_30': { per30Key: 'steals_per_30', unit: 'stl' },
  'blocks': { per30Key: 'blocks_per_30', unit: 'blk' },
  'blocks_per_30': { per30Key: 'blocks_per_30', unit: 'blk' },
  'turnovers': { per30Key: 'turnovers_per_30', unit: 'to' },
  'turnovers_per_30': { per30Key: 'turnovers_per_30', unit: 'to' },
  'three_point_made': { per30Key: 'three_point_field_goal_per_30', unit: '3pm' },
  'three_point_field_goal': { per30Key: 'three_point_field_goal_per_30', unit: '3pm' },
  'three_point_field_goal_per_30': { per30Key: 'three_point_field_goal_per_30', unit: '3pm' },
  'pra': { per30Key: 'pra_per_30', unit: 'pra' },
  'pra_per_30': { per30Key: 'pra_per_30', unit: 'pra' }
}

const statPer30Key = computed(() => {
  return statKeyMapping[props.statKey]?.per30Key || `${props.statKey}_per_30`
})

const statUnit = computed(() => {
  return statKeyMapping[props.statKey]?.unit || props.statKey
})

const getProjection = () => {
  if (!props.player) return {}
  if (props.player.projection) return props.player.projection
  // Try to find projection in player object
  return props.player
}

const projection = computed(getProjection)

const trim = ref(projection.value[props.statKey + '_trim'] ?? '')
const manual = ref(projection.value[props.statKey + '_manual'] ?? '')
const original = ref(projection.value[props.statKey + '_original'] ?? '')
const auto = ref(projection.value[props.statKey + '_auto'] ?? '')
const isDirty = ref(false)

watch(() => props.player, () => {
  trim.value = projection.value[props.statKey + '_trim'] ?? ''
  manual.value = projection.value[props.statKey + '_manual'] ?? ''
  auto.value = projection.value[props.statKey + '_auto'] ?? ''
}, { immediate: true })

watch(manual, (newVal) => {
  isDirty.value = newVal !== original.value
  emit('manual-change', { statKey: props.statKey, value: newVal })
})

async function saveChanges() {
  emit('close')

  console.log("save per30", projectionsStore.projectionsData)
  try {
    const response = await axios.post(apiBalancedProjectionsPath('projections/update-per30'), {
      playerId: props.player.player.id,
      statKey: props.statKey,
      market_type: props.statLabel.toLowerCase(),
      value: Number(manual.value),
      fixtureId: projectionsStore.projectionsData.id

    })

    const playerProjection = projectionsStore.projectionsData.fixture_projections.find(p => p.player_id === props.player.player.id)
    playerProjection[`${props.statKey}_manual`] = response.data[`${props.statKey}_manual`]

    if (!response.ok) throw new Error('Failed to update stat')

    // Update local state
    const updatedPlayer = { ...props.player }
    updatedPlayer.projection[`${props.statKey}_manual`] = Number(manual.value)
    emit('update:player', updatedPlayer)
    emit('close')
  } catch (error) {
    console.error('Error updating stat:', error)
    // You might want to show an error message to the user here
  }
}

function cancel() {
  manual.value = original.value
  emit('close')
}

const getStats = async () => {
  console.log("getStats", props.player)
  loading.value = true
  
  try {
    const response = await axios.post(apiBalancedProjectionsPath('projections/get-additional-stats-by-days'), {
      fixture_id: route.params.id,
      player_id: props.player.player.id,
      stat_key: props.statKey,
      days: 10 // Get data for last 10 days to cover all sections
    })

    // console.log("API Response:", response.data)
    
    // The API response structure is different - it's directly the data object
    if (response.data) {
      additionalStats.value = response.data
      
      // Calculate percentage differences
      calculatePercentageDifferences()
    }
  } catch (error) {
    console.error('Error fetching additional stats:', error)
  } finally {
    loading.value = false
  }
}

// Function to get the current displayed value (same logic as PlayerRow's getPerThirtyValue)
const getCurrentDisplayedValue = () => {
  // Check for trim value first
  if (trim.value != null && trim.value !== '0.00' && trim.value !== 0 && trim.value !== '') {
    return parseFloat(trim.value) || 0
  } 
  // Check for manual value
  else if (manual.value != null && manual.value !== '' && manual.value !== null && manual.value > 0) {
    return parseFloat(manual.value) || 0
  } 
  // Check for auto value
  else if (auto.value != null && auto.value !== '' && auto.value > 0) {
    return parseFloat(auto.value) || 0
  } 
  // Fall back to original value
  else if (original.value != null) {
    return parseFloat(original.value) || 0
  } 
  else {
    return 0
  }
}

const calculatePercentageDifferences = () => {
  if (!additionalStats.value || !additionalStats.value.last_games_per_30) {
    // Initialize with default values
    percentageDifference.value = { last3: 0, last6: 0, last10: 0 }
    return
  }
  
  // Get the current displayed value (could be trim, manual, auto, or original)
  const currentValue = getCurrentDisplayedValue()
  
  // Initialize percentage difference object
  percentageDifference.value = { last3: 0, last6: 0, last10: 0 }
  
  if (currentValue > 0) {
    // Calculate for last 3 games
    const last3Value = parseFloat(additionalStats.value.last_games_per_30.last_3_games_per_30?.[statPer30Key.value]) || 0
    percentageDifference.value.last3 = ((last3Value - currentValue) / currentValue) * 100
    
    // Calculate for last 6 games
    const last6Value = parseFloat(additionalStats.value.last_games_per_30.last_6_games_per_30?.[statPer30Key.value]) || 0
    percentageDifference.value.last6 = ((last6Value - currentValue) / currentValue) * 100
    
    // Calculate for last 10 games
    const last10Value = parseFloat(additionalStats.value.last_games_per_30.last_10_games_per_30?.[statPer30Key.value]) || 0
    percentageDifference.value.last10 = ((last10Value - currentValue) / currentValue) * 100
  }
}

// Computed property to check if any thresholds are reached
const hasThresholdAlert = computed(() => {
  if (!percentageDifference.value) return false
  
  // Get the actual percentage values, defaulting to 0 if undefined/null
  const last3Percent = Math.abs(percentageDifference.value.last3 || 0)
  const last6Percent = Math.abs(percentageDifference.value.last6 || 0)
  const last10Percent = Math.abs(percentageDifference.value.last10 || 0)
  
  // Check thresholds for each stat period
  const last3Threshold = last3Percent >= 50
  const last6Threshold = last6Percent >= 30
  const last10Threshold = last10Percent >= 20
  
  return last3Threshold || last6Threshold || last10Threshold
})

// Watch for changes in displayed values to re-calculate percentages
watch([trim, manual, auto, original], () => {
  if (additionalStats.value) {
    calculatePercentageDifferences()
  }
}, { deep: true })

onMounted(() => {
  getStats()
})
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style> 